import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiServices from "../service";

// Company Details -> FRN Details (§6.4, §6.5). Generic, not Transact-specific
// — IsActive gates Bulk Valuation eligibility for any provider. FRN/
// Description remain support-team-only, out-of-band; the toggle is the one
// tenant-facing write this page has.
export interface TenantFrnListItem {
  tenantFrnId: string;
  frn: string;
  description: string | null;
  isActive: boolean;
}

const initialState: {
  listItems: TenantFrnListItem[];
  loading: boolean;
  savingId: string | null;
} = {
  listItems: [],
  loading: false,
  savingId: null,
};

export const getFrnList = createAsyncThunk("frnDetails/getList", async (_: void, { rejectWithValue }) => {
  try {
    const response: any = await apiServices.frnDetails.list();
    if (response.data?.isError) {
      return rejectWithValue(response.data.error?.message?.[0] || "Failed to load FRNs");
    }
    // Default to [] — never fall back to the raw envelope object, which has
    // no .length/.map and crashes the page's list rendering.
    return (response.data?.data ?? []) as TenantFrnListItem[];
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to load FRNs");
  }
});

export const setFrnStatus = createAsyncThunk(
  "frnDetails/setStatus",
  async (payload: { id: string; isActive: boolean }, { rejectWithValue }) => {
    try {
      const response: any = await apiServices.frnDetails.setStatus(payload.id, payload.isActive);
      if (response.data?.isError) {
        return rejectWithValue(response.data.error?.message?.[0] || "Failed to update FRN status");
      }
      return payload;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to update FRN status");
    }
  }
);

export const frnDetailsSlice = createSlice({
  name: "frnDetails",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getFrnList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getFrnList.fulfilled, (state, action) => {
        state.loading = false;
        state.listItems = action.payload;
      })
      .addCase(getFrnList.rejected, (state) => {
        state.loading = false;
      })
      .addCase(setFrnStatus.pending, (state, action) => {
        state.savingId = action.meta.arg.id;
      })
      .addCase(setFrnStatus.fulfilled, (state, action) => {
        state.savingId = null;
        const item = state.listItems.find((i) => i.tenantFrnId === action.payload.id);
        if (item) item.isActive = action.payload.isActive;
      })
      .addCase(setFrnStatus.rejected, (state) => {
        state.savingId = null;
      });
  },
});

export default frnDetailsSlice.reducer;
